import {
  flaggedContentQueries,
  type FlaggedContentListPage,
} from "@circle-react/lib/queries/flaggedContent";
import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "react-query";

import { SETTINGS_TABLE_PAGE_SIZE } from "@/react/components/SettingsApp/SettingsShell/useSettingsTableLayout";

import type { UseModerationSettingsInfiniteQueryProps } from "../interfaces";

export const DEFAULT_PAGE_SIZE = SETTINGS_TABLE_PAGE_SIZE;

export const useModerationSettingsInfiniteQuery = (
  args?: UseModerationSettingsInfiniteQueryProps,
) => {
  const {
    from,
    content_type,
    flagged_by,
    order,
    query = "",
    perPage = DEFAULT_PAGE_SIZE,
    initialPage = 1,
    options = {},
    reason_type,
    status = "inbox",
  } = args ?? {};

  const queryConfig = flaggedContentQueries.list({
    from,
    content_type,
    flagged_by,
    initialPage,
    order,
    perPage,
    query,
    reason_type,
    status,
  });
  const queryOptions = {
    getNextPageParam: ({ has_next_page, page }: FlaggedContentListPage) =>
      has_next_page ? Number(page) + 1 : undefined,
    getPreviousPageParam: ({ page }: FlaggedContentListPage) =>
      page > 1 ? page - 1 : undefined,
    placeholderData: keepPreviousData,
    ...options,
  };

  const result = useInfiniteQuery({
    ...queryConfig,
    ...queryOptions,
  });
  const pages: FlaggedContentListPage[] = useMemo(
    () => result.data?.pages ?? [],
    [result.data?.pages],
  );
  const totalRecords = pages[0]?.count ?? 0;
  return {
    data: result.data,
    fetchNextPage: result.fetchNextPage,
    fetchPreviousPage: result.fetchPreviousPage,
    hasNextPage: result.hasNextPage,
    hasPreviousPage: result.hasPreviousPage,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    isFetchingPreviousPage: result.isFetchingPreviousPage,
    isLoading: result.isInitialLoading,
    pages,
    totalRecords,
  };
};
